import { useRef, useState } from "react";
import {
  Alert,
  Checkbox,
  FormControlLabel,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { CAMPOS_IMPORTACION_LEADS, leerFilasArchivo, prepararFilasImportacion } from "./leadImportFormat";
import { clasificarLeadsImportacion } from "./leadImportUtils";
import { toLocalISOString } from "../../utils/horaLocal";
export { CAMPOS_IMPORTACION_LEADS } from "./leadImportFormat";

function fechaSeguimientoDefault(dias = 3) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return toLocalISOString(d).slice(0, 10);
}

const createId = () =>
  window.crypto?.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2);

export function descargarFormatoImportacionLeads(canales = []) {
  const encabezados = CAMPOS_IMPORTACION_LEADS.map((campo) =>
    `${campo.label}${campo.obligatorio ? " (obligatorio)" : " (opcional)"}`
  );

  const canalEjemplo = canales[0] || "whatsapp";
  const valores = { nombre: "Juan Pérez", telefono: "70000000", cursoInteres: "Curso de inglés", montoEstimado: "1500", canal: canalEjemplo, observaciones: "Lead importado" };
  const ejemplo = CAMPOS_IMPORTACION_LEADS.map(c => valores[c.key] || "");

  const contenido = [
    encabezados.join(","),
    ejemplo.map((valor) => `"${valor}"`).join(","),
  ].join("\n");

  const blob = new Blob(["\ufeff" + contenido], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = "formato-importacion-leads.csv";
  enlace.click();
  URL.revokeObjectURL(url);
}

function ejecutarImportacion(store, { nuevos, duplicados, proximoSeguimiento, actualizarDuplicados }) {
  const canalesNuevos = [...nuevos, ...(actualizarDuplicados ? duplicados.map(d => d.lead) : [])].map(l => l.canal).filter(Boolean);
  if (canalesNuevos.length > 0) store.agregarCanalesSiFaltan(canalesNuevos);
  store.importarLeadsMasivo({ nuevos, duplicados, proximoSeguimiento, actualizarDuplicados });
}

export default function ImportarLeadsMasivo({ store }) {
  const inputRef = useRef(null);
  const [actualizarDuplicados, setActualizarDuplicados] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [errores, setErrores] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [pendiente, setPendiente] = useState(null);
  const [fechaSeguimiento, setFechaSeguimiento] = useState(() => fechaSeguimientoDefault());

  const etapaInicialImportacion = store.config.etapasPipeline?.includes("nuevo")
    ? "nuevo"
    : store.config.etapasPipeline?.[0] || "nuevo";

  const stats = store.config.importacionLeads || { totalImportados: 0, totalDuplicados: 0, eventos: [] };

  const finalizarImportacion = (lote, proximoSeguimiento = null) => {
    const { nuevos, duplicados, erroresValidacion } = lote;
    if (nuevos.length === 0 && duplicados.length === 0) {
      setErrores(erroresValidacion);
      return;
    }
    ejecutarImportacion(store, {
      nuevos,
      duplicados,
      actualizarDuplicados,
      proximoSeguimiento: duplicados.length ? proximoSeguimiento : null,
    });
    const partes = [];
    if (nuevos.length) partes.push(`${nuevos.length} lead(s) importado(s)`);
    if (duplicados.length) {
      partes.push(actualizarDuplicados ? `${duplicados.length} duplicado(s) revisado(s); se actualizaron sus columnas completadas (los archivados se conservan)` : `${duplicados.length} omitido(s) por duplicado (nombre + teléfono)`);
    }
    setMensaje(partes.join(". ") + ".");
    setErrores(erroresValidacion);
    setPendiente(null);
  };

  const procesarArchivo = async (event) => {
    const archivo = event.target.files?.[0];
    if (!archivo) return;

    setErrores([]);
    setMensaje("");
    setPendiente(null);
    setActualizarDuplicados(false);

    setProcesando(true);
    try {
      const filas = await leerFilasArchivo(archivo);
      if (filas.length < 2) throw new Error("El archivo no contiene registros para importar.");
      const { filasValidas, erroresValidacion, noReconocidas } = prepararFilasImportacion(filas, store.config);
      if (noReconocidas.length) erroresValidacion.push("Columnas no reconocidas: " + noReconocidas.join(", ") + ". Revisa sus nombres en el formato.");
      if (store.isOwner === false) filasValidas.forEach(({ lead }) => { lead.vendedorId = store.sellerId; });
      const { nuevos, duplicados } = clasificarLeadsImportacion([...store.leads, ...(store.leadsArchivados || [])], filasValidas, createId);
      const lote = { nuevos, duplicados, erroresValidacion };

      if (nuevos.length === 0 && duplicados.length === 0) {
        setErrores(erroresValidacion.length ? erroresValidacion : ["No hay filas válidas para importar."]);
        event.target.value = "";
        return;
      }

      if (duplicados.length > 0) {
        setFechaSeguimiento(fechaSeguimientoDefault(store.config.diasEntreSeguimiento || 3));
        setPendiente(lote);
        event.target.value = "";
        return;
      }

      finalizarImportacion(lote);
    } catch (error) {
      setErrores([error.message || "No se pudo leer el archivo. Revisa el formato."]);
    } finally {
      setProcesando(false);
      event.target.value = "";
    }
  };

  const canalesLista = (store.config.canales || []).join(", ");

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Importación masiva (Excel o CSV)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Obligatorios: <strong>Nombre</strong> y <strong>Teléfono</strong>. El nombre del archivo no importa.
        Si nombre y teléfono ya existen, no se crea otro lead; se registra el duplicado y puedes agendar seguimiento.
        Puedes subir XLSX, XLS o CSV (con comas o punto y coma). Las columnas opcionales se conservan; los valores inválidos se muestran como errores.
        Etapa inicial: {etapaInicialImportacion}.
      </Typography>
      {(stats.totalImportados > 0 || stats.totalDuplicados > 0) && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Calidad de importaciones: {stats.totalImportados} importados · {stats.totalDuplicados} duplicados omitidos
        </Typography>
      )}
      {canalesLista && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Canales configurados: {canalesLista}
        </Typography>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => descargarFormatoImportacionLeads(store.config.canales)}
        >
          Descargar formato CSV
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<UploadFileIcon />}
          disabled={procesando}
          onClick={() => inputRef.current?.click()}
        >
          {procesando ? "Procesando archivo…" : "Subir leads"}
        </Button>
      </Stack>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        hidden
        onChange={procesarArchivo}
      />
      {mensaje && (
        <Alert severity="success" sx={{ mt: 1.5 }}>
          {mensaje}
        </Alert>
      )}
      {errores.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Revisión del archivo:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {errores.slice(0, 15).map((error) => (
              <li key={error}>
                <Typography variant="body2">{error}</Typography>
              </li>
            ))}
          </Box>
          {errores.length > 15 && (
            <Typography variant="caption">… y {errores.length - 15} más</Typography>
          )}
        </Alert>
      )}

      <Dialog open={Boolean(pendiente)} onClose={() => setPendiente(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Leads duplicados</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {pendiente?.duplicados.length} fila(s) coinciden con un lead existente (mismo nombre y teléfono) y no crearán
            otro cliente. Puedes actualizar sus datos o conservarlos y agendar seguimiento. Los archivados se omiten.
          </Typography>
          {pendiente?.nuevos.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {pendiente.nuevos.length} lead(s) nuevos se importarán.
            </Typography>
          )}
          <FormControlLabel control={<Checkbox checked={actualizarDuplicados} onChange={e => setActualizarDuplicados(e.target.checked)} />} label="Actualizar datos de los duplicados con las columnas completadas del archivo" />
          <Typography variant="caption" display="block" sx={{ mb: 2 }}>Los campos vacíos no reemplazan datos existentes. Las actualizaciones quedan registradas en el historial. Los clientes archivados no se modifican.</Typography>
          {!actualizarDuplicados && <TextField
            label="Fecha límite de seguimiento (duplicados)"
            type="date"
            fullWidth
            size="small"
            value={fechaSeguimiento}
            onChange={(e) => setFechaSeguimiento(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendiente(null)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => finalizarImportacion(pendiente, fechaSeguimiento)}
            disabled={!actualizarDuplicados && !fechaSeguimiento}
          >
            Continuar importación
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
